import { forwardRef, useEffect, useRef, useState } from "react";
import {
  Button,
  Popover,
  VStack,
  Portal,
  HStack,
  Text,
  Flex,
} from "@chakra-ui/react";
import { Checkbox } from "./checkbox";
import type { Dispatch, SetStateAction } from "react";
import { LuLibraryBig } from "react-icons/lu";

export interface TableOption {
  label: string;
  value: string;
}

export interface FolderOption {
  label: string;
  value: string;
}

export interface TableSelectPopoverProps {
  tables?: TableOption[];
  folders?: FolderOption[];
  onSelect: (selectedTables: string[], selectedFolders: string[]) => void;
  triggerText?: string;
}

export const TableSelectPopover = forwardRef<
  HTMLButtonElement,
  TableSelectPopoverProps
>(
  (
    { tables = [], folders = [], onSelect, triggerText = "지식 저장소" },
    ref
  ) => {
    const [selectedTables, setSelectedTables] = useState<string[]>([]);
    const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
    const [isOpen, setIsOpen] = useState(false);

    const allTablesSelected =
      tables.length > 0 &&
      tables.every((t) => selectedTables.includes(t.value));
    const allFoldersSelected =
      folders.length > 0 &&
      folders.every((f) => selectedFolders.includes(f.value));

    const initialized = useRef(false);

    // 초기화 로직 개선: 컴포넌트 마운트 시에만 실행
    useEffect(() => {
      if (!initialized.current) {
        // 초기 로드 시 모든 항목 선택
        if (tables.length > 0) {
          setSelectedTables(tables.map((t) => t.value));
        }
        if (folders.length > 0) {
          setSelectedFolders(folders.map((f) => f.value));
        }
        initialized.current = true;
      }
    }, [tables, folders]);

    // props 변경 시 선택 상태 유지하면서 새로운 항목만 추가
    useEffect(() => {
      if (initialized.current) {
        setSelectedTables((prev) => {
          const newTables = tables.map((t) => t.value);
          const existingTables = prev.filter((t) => newTables.includes(t));
          return existingTables.length > 0 ? existingTables : newTables;
        });

        setSelectedFolders((prev) => {
          const newFolders = folders.map((f) => f.value);
          const existingFolders = prev.filter((f) => newFolders.includes(f));
          return existingFolders.length > 0 ? existingFolders : newFolders;
        });
      }
    }, [tables, folders]);

    const toggleItem = (
      value: string,
      _selected: string[],
      setSelected: Dispatch<SetStateAction<string[]>>
    ) => {
      setSelected((prev) =>
        prev.includes(value)
          ? prev.filter((v) => v !== value)
          : [...prev, value]
      );
    };

    const toggleAll = (
      items: { value: string }[],
      setSelected: Dispatch<SetStateAction<string[]>>
    ) => {
      setSelected((prev) => {
        const allSelected = items.every((item) => prev.includes(item.value));
        return allSelected ? [] : items.map((i) => i.value);
      });
    };

    useEffect(() => {
      onSelect(selectedTables, selectedFolders);
    }, [selectedTables, selectedFolders, onSelect]);

    return (
      <Popover.Root
        positioning={{ placement: "top-end" }}
        open={isOpen}
        onOpenChange={(details) => setIsOpen(details.open)}
      >
        <Popover.Trigger asChild>
          <Button
            ref={ref}
            width="90px"
            variant="ghost"
            borderRadius="2xl"
            border="1px solid #e2e8f0"
            // colorScheme="blue"
            size="sm"
            aria-expanded={isOpen}
            aria-haspopup="dialog"
          >
            <Flex align="center" gap={1}>
              <LuLibraryBig />
              <Text>{triggerText}</Text>
            </Flex>
          </Button>
        </Popover.Trigger>

        <Portal>
          <Popover.Positioner>
            <Popover.Content minW="400px">
              <Popover.CloseTrigger />
              <Popover.Arrow>
                <Popover.ArrowTip />
              </Popover.Arrow>
              <Popover.Body>
                <Flex gap={6} align="start">
                  {/* Table Column */}
                  <VStack align="stretch" gap={2} flex={1}>
                    <Text fontWeight="bold" fontSize="sm">
                      테이블
                    </Text>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => toggleAll(tables, setSelectedTables)}
                    >
                      {allTablesSelected ? "전체 해제" : "전체 선택"}
                    </Button>
                    {tables.map((table) => (
                      <HStack
                        key={table.value}
                        justify="space-between"
                        w="100%"
                        gap={2}
                      >
                        <Text fontSize="sm">{table.label}</Text>
                        <Checkbox
                          checked={selectedTables.includes(table.value)}
                          inputProps={{
                            onChange: () =>
                              toggleItem(
                                table.value,
                                selectedTables,
                                setSelectedTables
                              ),
                          }}
                        />
                      </HStack>
                    ))}
                  </VStack>

                  {/* Folder Column */}
                  <VStack align="stretch" gap={2} flex={1}>
                    <Text fontWeight="bold" fontSize="sm">
                      문서
                    </Text>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => toggleAll(folders, setSelectedFolders)}
                    >
                      {allFoldersSelected ? "전체 해제" : "전체 선택"}
                    </Button>
                    {folders.map((folder) => (
                      <HStack
                        key={folder.value}
                        justify="space-between"
                        w="100%"
                        gap={2}
                      >
                        <Text fontSize="sm">{folder.label}</Text>
                        <Checkbox
                          checked={selectedFolders.includes(folder.value)}
                          inputProps={{
                            onChange: () =>
                              toggleItem(
                                folder.value,
                                selectedFolders,
                                setSelectedFolders
                              ),
                          }}
                        />
                      </HStack>
                    ))}
                  </VStack>
                </Flex>
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    );
  }
);
